import {
  chunk,
  compact,
  floor,
  forEach,
  groupBy,
  orderBy,
  uniqBy,
} from 'lodash';

import { Edge, TileNode } from '../types';
import { getBoundingBoxForNodes, getNodeMeasuredWidthHeight } from './nodes';

// Each row contains at most chunkSize layout groups from the same section.
// A connected vertical stack is one group, regardless of its tile count.
// The nodes are sorted by height, so the larger nodes will likely
// be on the last row of a section. We also sort the nodes by the
// row index of their soruce node if they have an incoming edge.
// This helps to reduce the likelihood of edges crossing.
function getGridFromNodes(nodes: TileNode[], edges: Edge[], chunkSize: number) {
  const edgesBySource: Record<string, Edge[]> = groupBy(
    edges,
    (edge) => edge.source
  );

  const edgesByDestination: Record<string, Edge[]> = groupBy(
    edges,
    (edge) => edge.target
  );

  // Group nodes by section
  const sectionMap = groupBy(nodes, (node) => node.id.split(':')[0]);

  let grid: TileNode[][] = [];

  // Chunk nodes within each section
  forEach(sectionMap, (sectionNodes) => {
    // Sort nodes by height so nodes of similar height are next to each other
    const sortedSectionNodesSize = orderBy(
      sectionNodes,
      (node) => getNodeMeasuredWidthHeight(node).height
    );

    // If the node is a source, place source nodes first to improve edge routing
    // This creates a more natural top-down flow for connected nodes
    const sortedSectionNodes = orderBy(sortedSectionNodesSize, (node) => {
      // Determine if node is a source for any edges
      const isSource = (edgesBySource[node.id] ?? []).length > 0;
      // Determine if node is a target for any edges
      const isTarget = (edgesByDestination[node.id] ?? []).length > 0;

      if (isSource && !isTarget) {
        return 0; // Pure sources first
      } else if (isSource && isTarget) {
        return 1; // Intermediate nodes second
      } else {
        return 2; // Pure targets last
      }
    });

    const nodesToChunk = sortedSectionNodes.filter(
      (node) => !node.data.omitFromChunking
    );

    const nodesToNotChunk = sortedSectionNodes.filter(
      (node) => node.data.omitFromChunking
    );

    if (nodesToNotChunk.length > 0) {
      grid.push(nodesToNotChunk);
    }

    const nodesById = new Map(nodesToChunk.map((node) => [node.id, node]));
    const visited = new Set<string>();
    const groups: TileNode[][] = [];
    const addStack = (node: TileNode, group: TileNode[]): void => {
      if (visited.has(node.id)) return;
      visited.add(node.id);
      group.push(node);
      if (!node.data.stackY || node.data.centerY) return;
      // Keep connected stacks together even when recipients precede a source
      // in the size-based ordering. Centered term columns keep their own rules.
      const connections = [
        ...(edgesBySource[node.id] ?? []).map((edge) => edge.target),
        ...(edgesByDestination[node.id] ?? []).map((edge) => edge.source),
      ];
      connections.forEach((id) => {
        const connected = nodesById.get(id);
        if (connected?.data.stackY && !connected.data.centerY) {
          addStack(connected, group);
        }
      });
    };
    nodesToChunk.forEach((node) => {
      if (visited.has(node.id)) return;
      const group: TileNode[] = [];
      addStack(node, group);
      groups.push(group);
    });
    grid.push(...chunk(groups, chunkSize).map((row) => row.flat()));
  });

  // Within each row, sort the nodes by the order of the source node
  // so the edges are less likely to cross
  grid = grid.map((row) =>
    orderBy(row, (node) => {
      // Get index of the first source node
      const sourceNodeId = edgesByDestination[node.id]?.[0]?.source;

      if (!sourceNodeId) {
        // If there is no source node, push the node to the end of the row
        return row.length;
      }

      const sourceNodeRowIndex = grid
        .find((row) => row.some((node) => node.id === sourceNodeId))
        ?.findIndex((node) => node.id === sourceNodeId);

      if (sourceNodeRowIndex === undefined || sourceNodeRowIndex === -1) {
        return row.length;
      }

      return sourceNodeRowIndex;
    })
  );

  // Finally, within each row, sort the nodes by the external order parameter
  grid = grid.map((row) =>
    orderBy([...row.map((r, idx) => ({ ...r, idx }))], (node) => {
      const orderByNum = node.data.order ?? node.idx;
      return orderByNum;
    })
  );

  return grid;
}

// This function centers the nodes in the grid.
// It does this by calculating the center of the row and the center of the above row
// and then subtracting the center of the row from the center of the above row
// to get the xOffset. This xOffset is then applied to the nodes in the row.
function applyCenterGridNodes(grid: TileNode[][]) {
  // Center the nodes in the grid
  const getCenterOfRow = (row: TileNode[]) => {
    // Find the leftmost and rightmost edges of the row
    const leftEdge = Math.min(
      ...row
        .filter((node) => !node.data.omitFromCentering)
        .map((node) => node.position.x)
    );

    const rightEdge = Math.max(
      ...row
        .filter((node) => !node.data.omitFromCentering)
        .map((node) => node.position.x + (node.measured?.width ?? 0))
    );

    return (leftEdge + rightEdge) / 2;
  };

  grid.forEach((row) => {
    const aboveRow = grid[grid.indexOf(row) - 1];
    let xOffset = 0;

    if (aboveRow) {
      const centerOfAboveRow = getCenterOfRow(aboveRow);
      const centerOfRow = getCenterOfRow(row);
      xOffset = centerOfRow - centerOfAboveRow;
    }

    row.forEach((node) => {
      node.position.x -= xOffset;
    });
  });
}

// This function applies the grid layout to the nodes in the grid.
// It does this by calculating the x and y position for each node.
// If centerGrid is true, it will also center the nodes in the grid.
function applyGridLayoutNodes(
  grid: TileNode[][],
  paddingX: number,
  paddingY: number,
  centerGrid = true,
  edges: Edge[] = []
) {
  // Track yOffset for the row
  let yOffset = 0;

  // First pass: Calculate basic positions
  grid.forEach((row) => {
    // Track xOffset for the node
    let xOffset = 0;
    const stackRoots = new Map<string, string>();
    const stackBottoms = new Map<string, TileNode>();

    row.forEach((node, colIdx) => {
      let stackRoot: string | undefined;
      if (node.data.stackY) {
        const sourceId = edges.find(
          (edge) => edge.target === node.id && stackRoots.has(edge.source)
        )?.source;
        if (sourceId) {
          stackRoot = stackRoots.get(sourceId);
        } else if (node.data.centerY) {
          // Term diagrams use a centered column for unrelated beneficiaries.
          const previousNode = row[colIdx - 1];
          if (previousNode?.data.centerY) {
            stackRoot = stackRoots.get(previousNode.id);
          }
        }
      }
      const previousNode = stackRoot ? stackBottoms.get(stackRoot) : undefined;

      if (node.data.stackY && previousNode?.data.stackY) {
        // Siblings and nested recipients share their source's column.
        node.position.x = previousNode?.position.x ?? 0;
        node.position.y = previousNode?.position.y ?? 0;
        // Add the height of the previous node to the y position
        node.position.y +=
          getNodeMeasuredWidthHeight(previousNode).height + paddingY / 2;
      } else {
        node.position = { x: xOffset, y: yOffset };
        const width = getNodeMeasuredWidthHeight(node).width;

        xOffset += floor(width + paddingX, 1); // Apply paddingX between nodes
      }
      if (node.data.stackY) {
        const rootId = stackRoot ?? node.id;
        stackRoots.set(node.id, rootId);
        stackBottoms.set(rootId, node);
      }
    });

    yOffset =
      Math.max(
        yOffset,
        ...row.map(
          (node) => node.position.y + getNodeMeasuredWidthHeight(node).height
        )
      ) + paddingY;
  });

  // Apply centering to get nodes in proper positions
  if (centerGrid) {
    applyCenterGridNodes(grid);
  }

  // Create a mapping of node IDs to their source node IDs
  const targetToSourceMap: Record<string, string> = {};
  edges.forEach((edge) => {
    targetToSourceMap[edge.target] = edge.source;
  });

  // Create a mapping of node IDs to their positions in the grid
  const nodePositionMap: Record<string, { x: number; width: number }> = {};

  // Store the positions of all nodes
  grid.forEach((row) => {
    row.forEach((node) => {
      nodePositionMap[node.id] = {
        x: node.position.x,
        width: getNodeMeasuredWidthHeight(node).width || 0,
      };
    });
  });

  const uniqueSectionIds = compact(
    uniqBy(grid.flat(), (node) => node.data.sectionLabelId).map(
      (node) => node.data.sectionLabelId
    )
  );

  const sectionHeights: Record<string, number> = {};

  uniqueSectionIds.forEach((sectionId) => {
    const boundsForSection = getBoundingBoxForNodes(
      grid.flat().filter((n) => n.data.sectionLabelId === sectionId)
    );
    sectionHeights[sectionId] = boundsForSection.bottom - boundsForSection.top;
  });

  // This is a map of column index to nodes in that column
  const nodeColumns: Record<number, TileNode[]> = [];
  let colIdx = 0;

  // Build a record of the nodes indexed by their column index
  grid.forEach((row) => {
    row.forEach((node) => {
      if (node.data.stackY && (nodeColumns[colIdx]?.length ?? 0) > 0) {
        nodeColumns[colIdx]?.push(node);
      } else if (node.data.stackY) {
        nodeColumns[colIdx] = [node];
      } else {
        colIdx++;
        nodeColumns[colIdx] = [node];
        colIdx++;
      }
    });
  });

  // Additionally, if a node has a centerY property, we need to center its column
  Object.values(nodeColumns).forEach((column) => {
    const shouldCenterColumn = column.some((node) => node.data.centerY);

    if (!shouldCenterColumn || !column[0]?.data.sectionLabelId) {
      return;
    }

    const sectionId = column[0].data.sectionLabelId;
    if (!sectionId) return;

    const columnBounds = getBoundingBoxForNodes(column);
    const columnHeight = columnBounds.bottom - columnBounds.top;

    const sectionHeight = sectionHeights[sectionId];
    if (!sectionHeight) return;

    const centerY = (sectionHeight - columnHeight) / 2;
    column[0].position.y = centerY;
  });
}

interface GridLayoutProps {
  nodes: TileNode[];
  chunkSize: number;
  paddingX: number;
  paddingY: number;
  edges: Edge[];
}

// Modifies nodes such that they are positioned in a grid and edges are modified
// to break closer to the target node as the y travel of the edge increases.
export function gridLayout({
  nodes,
  chunkSize,
  paddingX,
  paddingY,
  edges,
}: GridLayoutProps): {
  grid: TileNode[][];
  edges: Edge[];
} {
  // Derive grid data structure from nodes, edges, and chunkSize
  const grid: TileNode[][] = getGridFromNodes(nodes, edges, chunkSize);

  // Apply the grid layout to the nodes in the grid including centering
  applyGridLayoutNodes(grid, paddingX, paddingY, true, edges);

  // Apply the external xOffset to the nodes
  grid.forEach((row) => {
    row.forEach((node) => {
      node.position.x = node.position.x + (node.data.xOffset ?? 0);
    });
  });

  // Apply the external yOffset to the nodes
  grid.forEach((row) => {
    row.forEach((node) => {
      node.position.y = node.position.y + (node.data.yOffset ?? 0);
    });
  });

  return {
    grid,
    edges,
  };
}
